// Config-driven field uniqueness — the shared, pure runtime shared by
// AddFormV1 and EditFormV1.
//
// An admin marks a field unique in Form Groups → Field → Validations; the
// stored entry is an ordinary validations[] row:
//
//   { type: 'unique', message: 'Email already present',
//     value: { collectionField, normalizer, skipBlank, validateOn } }
//
// Everything module-specific comes from that config. NOTHING in this file (or
// in the two form engines) may name a module, a collection or a field — the
// server resolves the collection, the stored field and the tenant scope from
// the module + the caller's claims.
//
// This module owns ALL of the uniqueness behaviour so neither form duplicates
// it: rule extraction, blank handling, normalization, the per-session cache,
// the stale-response guard, the pending-request counter that blocks submit, the
// antd rule factory (incl. "only after the synchronous rules pass") and the
// mapping of a submit-time 409 back onto form fields.

export const UNIQUE_VALIDATION_TYPE = 'unique';
export const ALREADY_PRESENT_CODE = 'already_present';
export const DUPLICATE_VALUE_CODE = 'DUPLICATE_VALUE';

const DEFAULT_MESSAGE = 'This value is already present';

// ── Rule extraction ──────────────────────────────────────────────────────────

function validationType(validation) {
  if (typeof validation === 'string') return validation;
  return validation?.type ?? validation?.rule ?? validation?.name;
}

// The admin editor stores the unique options as an OBJECT under `value` (the
// declared valueType is 'object', so the save path leaves it untouched). Older
// or hand-written config may put the same keys flat on the validation itself,
// so both shapes are accepted.
export function parseUniqueValidation(validation) {
  if (validationType(validation) !== UNIQUE_VALIDATION_TYPE) return null;
  if (typeof validation === 'string') {
    return { message: DEFAULT_MESSAGE, normalizer: 'trim', skipBlank: true, validateOn: 'blur' };
  }

  const raw = (validation.value && typeof validation.value === 'object') ? validation.value : validation;
  const validateOn = String(raw.validateOn ?? 'blur').toLowerCase();

  return {
    message: (typeof validation.message === 'string' && validation.message.trim())
      ? validation.message.trim()
      : DEFAULT_MESSAGE,
    // Kept only so the admin's configured target is inspectable client-side;
    // it is NEVER sent to the server (the server resolves the stored field).
    collectionField: raw.collectionField ?? '',
    normalizer: String(raw.normalizer ?? 'trim'),
    // skipBlank defaults to true; only an explicit `false` turns it off.
    skipBlank: raw.skipBlank !== false,
    validateOn: ['blur', 'change', 'submit'].includes(validateOn) ? validateOn : 'blur',
  };
}

export function getUniqueRule(field) {
  const validations = field?.validations ?? field?.validation ?? field?.rules ?? [];
  if (!Array.isArray(validations)) return null;
  for (const validation of validations) {
    const rule = parseUniqueValidation(validation);
    if (rule) return rule;
  }
  return null;
}

// Every unique-configured field in the form, flattened across groups. Used to
// map a submit-time 409 back onto a field when the transport lost the
// structured body (see extractDuplicateFieldErrors).
export function collectUniqueRules(groups = []) {
  const collected = [];
  (Array.isArray(groups) ? groups : []).forEach((group) => {
    (group?.fields ?? []).forEach((field) => {
      const rule = getUniqueRule(field);
      if (!rule || !field?.field) return;
      collected.push({ field: field.field, label: field.label ?? field.field, rule });
    });
  });
  return collected;
}

// ── Blank + normalization ────────────────────────────────────────────────────

// Blank = missing / null / empty / whitespace-only / empty list.
// Numeric ZERO and boolean FALSE are REAL values, not blanks — a "0" employee
// code or a `false` flag must still be uniqueness-checked.
export function isBlankUniqueValue(value) {
  if (value === undefined || value === null) return true;
  if (typeof value === 'number') return Number.isNaN(value);
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0;
  return String(value).trim() === '';
}

export function normalizeUniqueValue(value, normalizer = 'trim') {
  if (value === undefined || value === null) return '';
  const text = String(value);
  switch (String(normalizer)) {
    case 'exact': return text;
    case 'lower':
    case 'trimLower':
    case 'email': return text.trim().toLowerCase();
    case 'digitsOnly': return text.replace(/\D+/g, '');
    case 'trim':
    default: return text.trim();
  }
}

export function shouldCheckUniqueValue(rule, value) {
  if (!rule) return false;
  if (rule.skipBlank !== false && isBlankUniqueValue(value)) return false;
  return true;
}

// antd/rc-field-form filters rules by trigger. `[]` matches no trigger at all,
// so a submit-only rule runs exclusively inside form.validateFields().
export function uniqueValidateTriggers(rule) {
  if (!rule) return [];
  if (rule.validateOn === 'submit') return [];
  if (rule.validateOn === 'change') return ['onChange', 'onBlur'];
  return ['onBlur'];
}

// ── The checker (cache + staleness + pending) ────────────────────────────────

export const UNIQUE_STATUS = {
  SKIPPED: 'skipped',
  AVAILABLE: 'available',
  DUPLICATE: 'duplicate',
  STALE: 'stale',
  ERROR: 'error',
};

function cacheKey({ module, field, recordId, normalized }) {
  return `${module} ${field} ${recordId ?? ''} ${normalized}`;
}

/**
 * One checker per form session. Both form engines create exactly one and pass
 * it into getRules; it is the only place a uniqueness request is ever made.
 *
 * @param checkFieldUnique  the API function (injected so it can be mocked)
 * @param onPendingChange   (pendingCount) => void — drives the submit button
 */
export function createUniqueChecker({ checkFieldUnique, onPendingChange } = {}) {
  // Normalized-value cache: the same normalized value is never re-checked in
  // one form session (blur → submit → blur again is one request, not three).
  // Only DEFINITIVE outcomes are cached — a failed request must be retried.
  const cache = new Map();
  // Monotonic sequence. Every request takes the next number and records itself
  // as its field's latest; when a response comes back with a number that is no
  // longer the latest for that field, the user has typed on and the answer
  // describes an OLD value — it is dropped instead of overwriting the new one.
  const latestSeq = new Map();
  let seq = 0;
  let pending = 0;

  function setPending(next) {
    pending = next;
    if (typeof onPendingChange === 'function') onPendingChange(pending);
  }

  async function check({ module, field, rule, value, recordId, clientId, region } = {}) {
    if (!rule || !module || !field || typeof checkFieldUnique !== 'function') {
      return { status: UNIQUE_STATUS.SKIPPED };
    }
    if (!shouldCheckUniqueValue(rule, value)) {
      return { status: UNIQUE_STATUS.SKIPPED };
    }

    const normalized = normalizeUniqueValue(value, rule.normalizer);
    const key = cacheKey({ module, field, recordId, normalized });
    if (cache.has(key)) return cache.get(key);

    seq += 1;
    const mySeq = seq;
    latestSeq.set(field, mySeq);
    setPending(pending + 1);

    try {
      const result = await checkFieldUnique({
        module,
        field,
        value,
        // recordId is passed straight through: EditFormV1 supplies it (so the
        // record does not collide with itself), AddFormV1 never does.
        recordId,
        clientId,
        region,
      });

      if (latestSeq.get(field) !== mySeq) return { status: UNIQUE_STATUS.STALE };

      const outcome = result?.available === false
        ? {
          status: UNIQUE_STATUS.DUPLICATE,
          message: firstDuplicateMessage(result) || rule.message || DEFAULT_MESSAGE,
        }
        : { status: UNIQUE_STATUS.AVAILABLE };

      cache.set(key, outcome);
      return outcome;
    } catch (err) {
      if (latestSeq.get(field) !== mySeq) return { status: UNIQUE_STATUS.STALE };
      // FAIL SAFE. A network/server failure must never block a user from
      // typing or submitting — submit-time enforcement on the server is the
      // authoritative check, and it still runs. Not cached, so the next blur
      // retries.
      return { status: UNIQUE_STATUS.ERROR, error: err };
    } finally {
      setPending(Math.max(0, pending - 1));
    }
  }

  return {
    check,
    isPending: () => pending > 0,
    pendingCount: () => pending,
    // Exposed for tests / a form that reloads its config mid-session.
    reset: () => { cache.clear(); latestSeq.clear(); },
  };
}

function firstDuplicateMessage(result) {
  const fromErrors = (result?.errors ?? []).find((item) => item?.message)?.message;
  return fromErrors || result?.message || '';
}

// ── antd rule factory ────────────────────────────────────────────────────────

// Marker so getRules can find the unique rules again after the array is built
// (see attachUniqueSyncGuards).
const UNIQUE_RULE_FLAG = '__uniqueRule';

/**
 * Build the async antd rule for one `unique` validations entry.
 * `context` is supplied by the form engine: { checker, module, recordId,
 * clientId, region }. With no context (test harnesses, other callers of
 * getRules) the rule degrades to a no-op instead of throwing.
 */
export function buildUniqueValidationRule({ field, validation, context }) {
  const rule = parseUniqueValidation(validation);
  if (!rule) return { validator: () => Promise.resolve() };

  const fieldKey = field?.field ?? '';
  if (!context?.checker || !context?.module || !fieldKey) {
    return { validator: () => Promise.resolve() };
  }

  return {
    [UNIQUE_RULE_FLAG]: rule,
    validateTrigger: uniqueValidateTriggers(rule),
    validator: async (_, value) => {
      const outcome = await context.checker.check({
        module: context.module,
        field: fieldKey,
        rule,
        value,
        recordId: context.recordId,
        clientId: context.clientId,
        region: context.region,
      });
      if (outcome.status === UNIQUE_STATUS.DUPLICATE) {
        return Promise.reject(new Error(outcome.message || rule.message));
      }
      // skipped / available / stale / error all pass: a stale answer describes
      // a value the user has already replaced, and an error is handled by the
      // authoritative server-side check at submit.
      return Promise.resolve();
    },
  };
}

/**
 * The uniqueness call must never fire for a value that is ALREADY known
 * invalid (blank required field, malformed email, failed pattern) — that would
 * waste a round trip and stack a confusing second error under the field.
 *
 * antd runs a field's rules in parallel, so ordering alone cannot express
 * "after the synchronous rules". Instead each unique rule is re-wrapped with a
 * guard that first evaluates its sibling rules against the same value and
 * resolves immediately if any of them fails.
 */
export function attachUniqueSyncGuards(rules = []) {
  const list = Array.isArray(rules) ? rules : [];
  if (!list.some((rule) => rule && rule[UNIQUE_RULE_FLAG])) return list;

  const siblings = list.filter((rule) => rule && !rule[UNIQUE_RULE_FLAG]);
  return list.map((rule) => {
    if (!rule || !rule[UNIQUE_RULE_FLAG]) return rule;
    const inner = rule.validator;
    return {
      ...rule,
      validator: async (ruleArg, value) => {
        if (await hasSyncRuleError(siblings, value, ruleArg)) return Promise.resolve();
        return inner(ruleArg, value);
      },
    };
  });
}

// Minimal evaluator for the rule shapes this form engine actually produces:
// { required }, { pattern }, { len }, { type:'url' } and custom { validator }.
// Any rule it cannot interpret is treated as passing — the guard exists to
// suppress a redundant request, never to invent a failure.
export async function hasSyncRuleError(rules = [], value, ruleArg = {}) {
  for (const rule of rules) {
    if (!rule || typeof rule === 'string') continue;
    if (rule.required && isBlankUniqueValue(value)) return true;
    if (!isBlankUniqueValue(value)) {
      if (rule.pattern instanceof RegExp && !new RegExp(rule.pattern.source, rule.pattern.flags).test(String(value))) return true;
      if (rule.len != null && String(value).length !== Number(rule.len)) return true;
    }
    if (typeof rule.validator === 'function') {
      try {
        await rule.validator(ruleArg, value);
      } catch {
        return true;
      }
    }
  }
  return false;
}

// ── Submit-time 409 → field errors ───────────────────────────────────────────

function parseMaybeJson(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function duplicateBody(source) {
  if (!source) return null;
  if (typeof source === 'string') return parseMaybeJson(source);
  // An Error thrown by the create/update services: the structured body rides
  // on `.data` (createModuleRecord) or `.response` (fetchJsonWithAuth).
  const candidates = [source.data, source.response, source, parseMaybeJson(source.message)];
  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object') continue;
    if (Array.isArray(candidate.errors) || candidate.code === DUPLICATE_VALUE_CODE) return candidate;
  }
  return null;
}

/**
 * The antd form path a duplicate error must be attached to.
 *
 * A field inside a REPEATABLE (addRow) group is registered under
 * [groupName, rowIndex, fieldKey] — its Form.List is named after the group — so
 * an error reported with only the field key would either land nowhere or, worse,
 * on a same-named field elsewhere in the form. The server sends `group` and
 * `rowIndex` alongside `field` for exactly this case; both are absent for an
 * ordinary field, which keeps the historical plain-string name.
 */
export function duplicateErrorName({ field, group, rowIndex }) {
  const key = String(field ?? '');
  const row = Number(rowIndex);
  if (group && Number.isInteger(row) && row >= 0) {
    // A row field's own key may itself be a dotted path within the row object.
    return [String(group), row, ...key.split('.')];
  }
  return key;
}

/**
 * Map a failed create/update into per-field duplicate errors.
 *
 * The `field` in the response is the FRONTEND field key — which is not always
 * the stored Mongo field (a form field `altEmail` may be stored as
 * `alternateEmail`) — so the returned name is always taken from the response,
 * never from the configured collectionField.
 *
 * `uniqueRules` (from collectUniqueRules) is the fallback path: one of the
 * update services flattens an error body down to its message string, which
 * loses `errors[]`. When the surviving message is exactly the message an admin
 * configured for a unique field, that identifies the field unambiguously
 * without any module or field name being hardcoded here.
 *
 * Returns [] when the failure is not a duplicate, so callers keep their normal
 * error handling.
 */
export function extractDuplicateFieldErrors(source, { uniqueRules = [] } = {}) {
  const body = duplicateBody(source);
  if (body) {
    const errors = (Array.isArray(body.errors) ? body.errors : [])
      .filter((item) => item?.field)
      .map((item) => {
        const mapped = {
          field: item.field,
          // `name` is what form.setFields needs; `field` stays for callers (and
          // tests) that only care which field key was reported.
          name: duplicateErrorName(item),
          message: item.message || body.message || DEFAULT_MESSAGE,
        };
        // Only present for a repeatable-group duplicate — never emitted as
        // `undefined`, so an ordinary duplicate is the exact object it always was
        // plus `name`.
        if (Array.isArray(mapped.name)) {
          mapped.group = String(item.group);
          mapped.rowIndex = Number(item.rowIndex);
        }
        return mapped;
      });
    if (errors.length) return errors;
  }

  const message = String(
    (body && (body.message || body.error))
    ?? (typeof source === 'string' ? source : source?.message)
    ?? '',
  ).trim();
  if (!message) return [];

  const isDuplicateStatus = source?.status === 409 || body?.code === DUPLICATE_VALUE_CODE;
  const matched = uniqueRules.filter(
    (entry) => String(entry?.rule?.message ?? '').trim().toLowerCase() === message.toLowerCase(),
  );
  if (matched.length && (isDuplicateStatus || matched.length === 1)) {
    // Fallback path: the structured body was flattened to a message, so the row
    // index is gone. The field is still identified, and the error lands on the
    // field rather than nowhere — see the note in collectUniqueRules.
    return matched.map((entry) => ({ field: entry.field, name: entry.field, message }));
  }
  return [];
}

export default createUniqueChecker;
